import React, { useState, useEffect, useCallback } from 'react';
import { 
  Image, Film, Layers, ExternalLink, Download, Calendar,
  Filter, CheckCircle, ChevronDown, RefreshCw
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FORMAT_ICONS = {
  'Post': Image,
  'Story': Film,
  'Reel': Film,
  'Post Carrusel': Layers,
  'Carrusel': Layers,
};

const FORMAT_COLORS = {
  'Post': 'bg-blue-500/10 text-blue-600 border-blue-200',
  'Story': 'bg-purple-500/10 text-purple-600 border-purple-200',
  'Reel': 'bg-pink-500/10 text-pink-600 border-pink-200',
  'Post Carrusel': 'bg-[rgba(232,106,31,0.1)] text-amber-600 border-amber-200',
  'Carrusel': 'bg-[rgba(232,106,31,0.1)] text-amber-600 border-amber-200',
};

export default function ApprovedGalleryTab({ clientId, token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [selectedFormat, setSelectedFormat] = useState(null);
  const [showMonthDropdown, setShowMonthDropdown] = useState(false);
  const [showFormatDropdown, setShowFormatDropdown] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (selectedMonth) {
        params.year = selectedMonth.year;
        params.month = selectedMonth.month;
      }
      if (selectedFormat) {
        params.format_filter = selectedFormat;
      }
      const res = await axios.get(`${API_URL}/api/weekly-deliverables/portal/${clientId}/gallery`, {
        params,
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setData(res.data);
    } catch (e) {
      console.error('Error loading gallery:', e);
    } finally {
      setLoading(false);
    }
  }, [clientId, token, selectedMonth, selectedFormat]);

  useEffect(() => { load(); }, [load]);

  const pieces = data?.pieces || [];
  const months = data?.available_months || [];
  const formats = data?.available_formats || [];

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center py-16" data-testid="gallery-loading">
        <RefreshCw className="w-5 h-5 text-orange-400 animate-spin" />
        <span className="ml-2 text-[#AEAEB2] text-sm">Cargando galería...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="approved-gallery-tab">
      {/* Header with filters */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-green-500" />
            Contenido Aprobado
          </h2>
          <p className="text-sm text-[#AEAEB2]">{data?.total_approved || 0} pieza{(data?.total_approved || 0) !== 1 ? 's' : ''} aprobada{(data?.total_approved || 0) !== 1 ? 's' : ''}</p>
        </div>

        <div className="flex gap-2">
          {/* Month filter */}
          <div className="relative">
            <button
              onClick={() => { setShowMonthDropdown(!showMonthDropdown); setShowFormatDropdown(false); }}
              className="flex items-center gap-1.5 px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm text-gray-700 transition"
              data-testid="month-filter-btn"
            >
              <Calendar className="w-4 h-4" />
              {selectedMonth ? selectedMonth.label : 'Todos los meses'}
              <ChevronDown className="w-3.5 h-3.5" />
            </button>
            {showMonthDropdown && (
              <div className="absolute right-0 top-full mt-1 bg-white rounded-lg shadow-lg border z-20 py-1 min-w-[160px]" data-testid="month-filter-dropdown">
                <button
                  onClick={() => { setSelectedMonth(null); setShowMonthDropdown(false); }}
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 ${!selectedMonth ? 'text-orange-600 font-medium' : 'text-gray-700'}`}
                >
                  Todos los meses
                </button>
                {months.map((m, i) => (
                  <button
                    key={i}
                    onClick={() => { setSelectedMonth(m); setShowMonthDropdown(false); }}
                    className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex justify-between ${
                      selectedMonth?.year === m.year && selectedMonth?.month === m.month ? 'text-orange-600 font-medium' : 'text-gray-700'
                    }`}
                  >
                    {m.label}
                    <span className="text-[#AEAEB2] text-xs">{m.count}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Format filter */}
          <div className="relative">
            <button
              onClick={() => { setShowFormatDropdown(!showFormatDropdown); setShowMonthDropdown(false); }}
              className="flex items-center gap-1.5 px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm text-gray-700 transition"
              data-testid="format-filter-btn"
            >
              <Filter className="w-4 h-4" />
              {selectedFormat || 'Todos los tipos'}
              <ChevronDown className="w-3.5 h-3.5" />
            </button>
            {showFormatDropdown && (
              <div className="absolute right-0 top-full mt-1 bg-white rounded-lg shadow-lg border z-20 py-1 min-w-[140px]" data-testid="format-filter-dropdown">
                <button
                  onClick={() => { setSelectedFormat(null); setShowFormatDropdown(false); }}
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 ${!selectedFormat ? 'text-orange-600 font-medium' : 'text-gray-700'}`}
                >
                  Todos los tipos
                </button>
                {formats.map((f, i) => (
                  <button
                    key={i}
                    onClick={() => { setSelectedFormat(f.format); setShowFormatDropdown(false); }}
                    className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex justify-between ${
                      selectedFormat === f.format ? 'text-orange-600 font-medium' : 'text-gray-700'
                    }`}
                  >
                    {f.format}
                    <span className="text-[#AEAEB2] text-xs">{f.count}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Gallery Grid */}
      {pieces.length === 0 ? (
        <div className="text-center py-16" data-testid="gallery-empty">
          <CheckCircle className="w-10 h-10 text-[#6E6E73] mx-auto mb-3" />
          <p className="text-[#AEAEB2] text-sm">
            {selectedMonth || selectedFormat
              ? 'No hay contenido aprobado con estos filtros'
              : 'Aún no hay contenido aprobado'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="gallery-grid">
          {pieces.map((piece) => {
            const Icon = FORMAT_ICONS[piece.format] || Image;
            const colorClass = FORMAT_COLORS[piece.format] || 'bg-gray-100 text-[#6E6E73] border-gray-200';
            const monthNames = ['', 'Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

            return (
              <div
                key={piece.piece_id}
                className="bg-white border rounded-xl overflow-hidden hover:shadow-md transition-shadow"
                data-testid={`gallery-piece-${piece.piece_id}`}
              >
                {/* Card header with format icon */}
                <div className="bg-gray-50 px-4 py-3 flex items-center justify-between border-b">
                  <div className="flex items-center gap-2">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${colorClass}`}>
                      <Icon className="w-3 h-3" />
                      {piece.format}
                    </span>
                    <span className="text-[10px] text-[#AEAEB2]">
                      {monthNames[piece.month]} {piece.year} · Sem {piece.week_number}
                    </span>
                  </div>
                  <CheckCircle className="w-4 h-4 text-green-500" />
                </div>

                {/* Card body */}
                <div className="p-4">
                  <h3 className="font-medium text-gray-900 text-sm mb-1">{piece.name}</h3>
                  {piece.note && (
                    <p className="text-xs text-[#AEAEB2] mb-3">{piece.note}</p>
                  )}
                  <div className="flex items-center justify-between mt-3">
                    <span className="text-[10px] text-[#AEAEB2]">
                      {piece.uploaded_by_name}
                    </span>
                    {piece.drive_url && (
                      <a
                        href={piece.drive_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1 text-xs text-orange-600 hover:text-orange-700 font-medium"
                        data-testid={`download-piece-${piece.piece_id}`}
                      >
                        <Download className="w-3.5 h-3.5" />
                        Descargar
                      </a>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
